/* eslint-disable react/require-default-props */

import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkSmallPictureIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/picture';
import cx from 'classnames';

import SlideshowViewModal from './SlideshowViewModal';
import { useImageGalleryState } from './utils';

import type { ImageEntry } from './types';
import type { GalleryLayout } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ImageGallery';

import STYLES from './ImageGallery.module.scss';

type Props = {
  aspectRatio?: string;
  // Explicit layout override. When omitted, the layout falls back to the
  // legacy count-based behaviour below. This prop exists so that specific
  // page types (currently only destination hub pages) can opt in to a
  // capped layout without changing the default behaviour for every other
  // consumer of this shared component (package holidays, sponsored
  // airline/destination explorers, custom/creator pages, etc).
  galleryLayout?: GalleryLayout;
  images: ImageEntry[];
  translations: {
    viewPhotosButton: string;
    closeModal: string;
    previousImage: string;
    nextImage: string;
  };
};

const layoutClassNames = {
  layout1: STYLES.layout1,
  layout3: STYLES.layout3,
  layout6: STYLES.layout6,
};

const galleryLayoutToCSS: Record<GalleryLayout, string> = {
  oneImage: STYLES.layout1,
  threeImages: STYLES.layout3,
  sixImages: STYLES.layout3,
};

const ImageGallery = ({
  aspectRatio,
  galleryLayout,
  images,
  isCreatorPage = false,
  translations,
}: Props & { isCreatorPage?: boolean }) => {
  const { close, open, state } = useImageGalleryState();
  const imagesCount = images.length;
  const containerClassNames = [STYLES.grid];

  if (galleryLayout) {
    containerClassNames.push(galleryLayoutToCSS[galleryLayout]);
  } else if (imagesCount < 3) {
    containerClassNames.push(layoutClassNames.layout1);
  } else if (imagesCount < 6) {
    containerClassNames.push(layoutClassNames.layout3);
  } else {
    containerClassNames.push(layoutClassNames.layout6);
  }
  const mbdParams = {
    component_name: 'creators_image_gallery',
    string_properties: {
      vertical: 'creators',
      page_type: 'creator-destination',
    },
  };

  return (
    <>
      <div
        className={STYLES.container}
        data-testid="image-gallery"
        data-tracking-element-id={
          isCreatorPage ? mbdParams.component_name : undefined
        }
        data-tracking-common-params={
          isCreatorPage ? JSON.stringify(mbdParams) : undefined
        }
      >
        <div className={cx(containerClassNames)} role="list">
          {images.slice(0, 6).map((image, index) => (
            <div key={image.url} role="listitem" className={STYLES.previewItem}>
              <button
                aria-label={image.location || ''}
                type="button"
                className={STYLES.previewItemButton}
                onClick={() => open(index)}
              >
                <img
                  className={STYLES.image}
                  alt={image.altText ? image.altText : ''}
                  src={image.url}
                  loading="lazy"
                />
              </button>
            </div>
          ))}
        </div>
        <div className={STYLES.openSlideshowViewButtonContainer}>
          <BpkButton
            className={STYLES.viewPhotosButton}
            onClick={() => open(0)}
            data-testid="open-image-gallery-btn"
            type={BUTTON_TYPES.primaryOnDark}
          >
            <BpkSmallPictureIcon aria-hidden />
            {translations.viewPhotosButton}
          </BpkButton>
        </div>
      </div>
      {state.isOpen ? (
        <SlideshowViewModal
          aspectRatio={aspectRatio}
          onClose={close}
          images={images}
          initialImageIndex={state.initialImageIndex}
          translations={translations}
        />
      ) : null}
    </>
  );
};

export default ImageGallery;
